import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Dropdown, Avatar, Badge, theme } from 'antd';
import {
  DashboardOutlined,
  UnorderedListOutlined,
  CalendarOutlined,
  TeamOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ToolOutlined,
  ShopOutlined,
  FilePdfOutlined,
  PictureOutlined,
  UsergroupAddOutlined,
  GlobalOutlined,
  BankOutlined,
  BarChartOutlined,
  ShoppingOutlined,
  ShoppingCartOutlined,
  SwapOutlined,
  TagsOutlined,
  MessageOutlined,
  BellOutlined,
  ApartmentOutlined,
  MailOutlined,
  CommentOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import BotonActualizar from './BotonActualizar';
import useSidebarResponsive from './useSidebarResponsive';
import FondoSidebarMobile from './FondoSidebarMobile';
import { buyerService } from '../../services/api';

const { Header, Sider, Content } = Layout;

/** ¿La cabaña tiene contratado este recurso opcional?
 *
 *  A nivel de módulo y recibiendo `recursos` por parámetro: adentro del
 *  componente cerraba sobre `recursos` sin estar en las deps del useMemo del
 *  menú. Funcionaba —`recursos` sí era dependencia— pero el día que la función
 *  leyera algo más, el menú quedaba viejo sin que nada avisara.
 */
const tieneRecurso = (recursos, codigo) => Boolean(recursos?.includes(codigo));

/** Submenú que le corresponde a una ruta. A nivel de módulo: sólo depende del
 *  pathname, así puede entrar como dependencia de un efecto sin recrearse. */
const submenuDeLaRuta = (pathname) => {
  if (pathname.startsWith('/animales')) return ['animales-menu'];
  if (pathname.startsWith('/herramientas')) return ['herramientas-menu'];
  if (pathname.startsWith('/ventas')) return ['ventas-menu'];
  return [];
};

const MainLayout = () => {
  const {
    esMobile, collapsed, alternar, cerrarSiEsMobile,
    propsSider, zIndexSider, marginLeftContenido,
  } = useSidebarResponsive();
  const [noLeidasCount, setNoLeidasCount] = useState(0);
  const { user, logout, recursos, soloLectura } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // El menú pasó de `defaultOpenKeys` a controlado: ese prop se lee UNA sola
  // vez, al montar, y MainLayout no se vuelve a montar al navegar porque
  // envuelve al <Outlet/>. Resultado: entrar a /animales/… desde otra sección
  // dejaba el ítem resaltado adentro de un submenú cerrado, o sea invisible.
  // Sólo se veía bien recargando la página parado en esa URL.
  const [openKeys, setOpenKeys] = useState(() => submenuDeLaRuta(location.pathname));

  useEffect(() => {
    /* EN MOBILE NO CORRE — ver el bloque de abajo. Con el panel cerrado no hay
     * ningun menu visible que revelar, asi que reabrir el submenu de la ruta no
     * ayuda a nadie y encima deja un popup flotando sobre la pantalla recien
     * navegada. En desktop si importa y sigue igual. */
    if (esMobile) return;
    const delaRuta = submenuDeLaRuta(location.pathname);
    if (delaRuta.length === 0) return;
    setOpenKeys(prev => (
      // Unión, no reemplazo: no se cierra lo que el usuario abrió a mano.
      // Y se compara antes de setear para no disparar un render de más.
      delaRuta.every(k => prev.includes(k)) ? prev : [...new Set([...prev, ...delaRuta])]
    ));
  }, [location.pathname, esMobile]);

  /* EL SUBMENU QUE QUEDABA FLOTANDO SOBRE LA PANTALLA EN MOBILE.
   *
   * Sintoma: tocabas una opcion de adentro de un submenu, el panel se cerraba y
   * navegabas bien, pero el submenu seguia visible encima del contenido hasta
   * que tocabas cualquier lado.
   *
   * Causa: cuando un <Sider> esta `collapsed`, antd le pasa `inlineCollapsed`
   * al <Menu> por contexto, y un menu inline colapsado deja de renderizar los
   * submenus abiertos como lista y los renderiza como POPUP en un portal
   * colgado del <body>. Un portal no vive adentro del Sider, asi que el
   * `collapsedWidth: 0` de mobile no lo recorta: queda flotando. Y el efecto de
   * arriba, al ver el pathname nuevo, volvia a meter la key en `openKeys` justo
   * cuando el panel se estaba cerrando.
   *
   * Arreglo: en mobile `openKeys` sigue al panel. Se cierra -> vacio, no hay
   * popup posible. Se abre -> el submenu de la ruta actual, para que la opcion
   * resaltada se vea sin tener que desplegar a mano (que es lo que el efecto de
   * arriba resuelve en desktop).
   *
   * VA EN FASE DE RENDER, no en un useEffect: el efecto corre DESPUES del
   * paint, o sea que se veria un frame con el popup antes de que lo limpie. Es
   * el mismo patron —y por la misma razon— que el reset de `abiertoMobile` en
   * useSidebarResponsive. */
  const panelAbierto = !collapsed;
  const [panelAbiertoAntes, setPanelAbiertoAntes] = useState(panelAbierto);
  if (panelAbiertoAntes !== panelAbierto) {
    setPanelAbiertoAntes(panelAbierto);
    if (esMobile) {
      setOpenKeys(panelAbierto ? submenuDeLaRuta(location.pathname) : []);
    }
  }
  const { token: { colorBgContainer } } = theme.useToken();

  useEffect(() => {
    let vigente = true;
    const fetchNoLeidas = async () => {
      try {
        const res = await buyerService.getNoLeidasCount();
        if (vigente) setNoLeidasCount(res.data?.count || 0);
      } catch {
        // Silencioso a proposito: es un contador que se refresca solo cada
        // minuto en segundo plano. Nadie apreto nada, y un cartel de error
        // por no poder pintar un badge molesta mas de lo que informa. Si
        // falla, el badge queda como estaba y el resto del panel anda igual
        // — es la excepcion (c) de Skills/react-rules.md, mejora progresiva
        // que degrada sola.
      }
    };
    fetchNoLeidas();
    const intervalo = setInterval(fetchNoLeidas, 60000);
    // `vigente` evita setState despues de desmontar si la respuesta llega
    // tarde. Igual que en ClienteLayout y PujaLayout: las tres copias de este
    // efecto se comportan igual. (El arreglo de fondo -un hook compartido-
    // sigue en PENDIENTES.)
    return () => { vigente = false; clearInterval(intervalo); };
  }, []);

  // Items del menú lateral, DERIVADOS de los recursos de la membresía.
  //
  // POR QUÉ DECLARATIVO Y NO UNA LISTA POR PLAN. Antes esto se armaba con
  // pushes y sólo dos items miraban el recurso; los otros dieciocho se mostraban
  // siempre. Una cabaña invitada —que sólo tiene recursos de lectura— veía
  // "Crear animal", "Micrositio" y "Ventas", y cada clic terminaba en un 403.
  // Eso no es captar clientes: es enseñarle que el producto no anda. Le
  // ofrecemos algo y se lo negamos un clic después.
  //
  // Con el recurso como DATO, el menú de cada plan sale solo. El día que se
  // invente otro plan no hay que tocar este archivo: alcanza con su matriz de
  // recursos. `recurso: null` = visible para todos.
  //
  // `soloLectura` lo manda el backend derivado de `recursos.es_lectura`, y
  // cubre los dos items que no tienen un recurso propio que los represente.
  const menuItems = useMemo(() => {
    const visible = (item) => {
      // Operar = organizar remates (`crud_eventos`). Desde que la cabaña
      // invitada carga sus propios animales (2026-10-28) ya no es de sólo
      // lectura, pero sigue sin vender ni autorizar compradores: eso es del
      // remate, y el remate es del organizador.
      if (item.soloSiOpera && (soloLectura || !tieneRecurso(recursos, 'crud_eventos'))) return false;
      if (item.rol !== undefined) return user?.id_rol === item.rol;
      return !item.recurso || tieneRecurso(recursos, item.recurso);
    };

    const definicion = [
      { key: '/dashboard', icon: <DashboardOutlined />, label: 'Dashboard' },
      {
        key: 'animales-menu', icon: <UnorderedListOutlined />, label: 'Animales',
        children: [
          { key: '/animales', icon: <UnorderedListOutlined />, label: 'Gestión Animales', recurso: 'ver_animales' },
          { key: '/animales/familiares', icon: <UsergroupAddOutlined />, label: 'Familiares', recurso: 'crud_familiares' },
          // El catálogo (arriba) es QUIÉNES son los familiares; esto es a QUÉ
          // animal pertenece cada uno. Van separados porque el catálogo se
          // carga una vez y la vinculación se rehace en cada remate.
          { key: '/animales/vincular-familiares', icon: <ApartmentOutlined />, label: 'Vincular familiares', recurso: 'crud_familiares' },
          { key: '/animales/fotos-videos', icon: <PictureOutlined />, label: 'Fotos y Videos', recurso: 'fotos_videos' },
        ],
      },
      { key: '/eventos', icon: <CalendarOutlined />, label: 'Eventos', recurso: 'ver_eventos' },
      // Los remates de OTRAS cabañas a los que me invitaron a consignar. Va con
      // `ver_eventos` y no con `multi_cabanas`: el invitado no ORGANIZA un
      // remate compartido, participa de uno.
      { key: '/invitaciones', icon: <MailOutlined />, label: 'Invitaciones', recurso: 'ver_eventos' },
      // `ver_cabanas` y no `multi_cabanas`: toda cabaña tiene al menos una, asi
      // que VERLA no puede depender de la herramienta del que organiza remates
      // compartidos. El nombre de su cabaña es lo que el comprador lee en el
      // catalogo, y su dueña es la unica que puede detectar un error ahi.
      { key: '/cabanas', icon: <BankOutlined />, label: 'Cabañas', recurso: 'ver_cabanas' },
      // Clientes y Ventas son la administración de una cabaña que OPERA. No hay
      // un recurso que las represente —vender no es una feature de plan, es lo
      // que hace la herramienta— así que se apoyan en `soloLectura`.
      { key: '/clientes', icon: <TeamOutlined />, label: 'Clientes', soloSiOpera: true },
      {
        key: 'herramientas-menu', icon: <ToolOutlined />, label: 'Herramientas',
        children: [
          { key: '/herramientas/micrositio', icon: <GlobalOutlined />, label: 'Micrositio', recurso: 'config_identidad' },
          { key: '/herramientas/generar-pdf', icon: <FilePdfOutlined />, label: 'Generar PDF', recurso: 'generar_pdf' },
          // El mismo recurso que gatea los endpoints (`catalogo_genetica`): si el
          // menu se gateara con otro, el productor veria la opcion y la pantalla
          // le daria 403 al cargar.
          { key: '/herramientas/catalogo-genetica', icon: <ShopOutlined />, label: 'Catálogo de genética', recurso: 'catalogo_genetica' },
          // La bandeja de consultas que llegan por la ficha pública del
          // catálogo. Mismo recurso que gatea el catálogo en sí: no existe
          // sin él.
          { key: '/herramientas/consultas-catalogo', icon: <CommentOutlined />, label: 'Consultas del catálogo', recurso: 'catalogo_genetica' },
        ],
      },
      {
        key: 'ventas-menu', icon: <ShoppingOutlined />, label: 'Venta de Animales', soloSiOpera: true,
        children: [
          { key: '/ventas/animales', icon: <ShoppingCartOutlined />, label: 'Ventas' },
          { key: '/ventas/tablero', icon: <BarChartOutlined />, label: 'Tablero de ventas' },
          { key: '/ventas/movimientos', icon: <SwapOutlined />, label: 'Movimientos' },
        ],
      },
      // ⚠️ ESTE CHECK ESTA MAL Y SE DEJA A PROPOSITO — ver PENDIENTES.md.
      // `id_rol === 1` a secas matchea al admin de CABAÑA, no al de plataforma
      // (el de plataforma nunca llega a este layout: rutaPanel lo manda a
      // /puja/*). Arreglarlo SOLO aca deja la pantalla sin ninguna via de
      // acceso, porque el menu de plataforma vive en PujaLayout. El arreglo de
      // verdad es mover Categorias al panel de Puja y cerrar el backend, y eso
      // merece su propio cambio.
      { key: '/categorias', icon: <TagsOutlined />, label: 'Categorías', rol: 1 },
      { key: '/consultas', icon: <MessageOutlined />, label: 'Consultas' },
      { key: '/analytics', icon: <BarChartOutlined />, label: 'Analytics', recurso: 'analytics' },
    ];

    return definicion
      .filter(visible)
      .map(({ recurso, rol, soloSiOpera, children, ...item }) => {
        if (!children) return item;
        const hijos = children.filter(visible)
          .map(({ recurso: _r, rol: _l, soloSiOpera: _o, ...hijo }) => hijo);
        // Un submenú sin hijos visibles es una flecha que no abre nada.
        return hijos.length ? { ...item, children: hijos } : null;
      })
      .filter(Boolean);
  }, [recursos, soloLectura, user?.id_rol]);

  // Items del dropdown de usuario
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Mi Perfil',
    },
    // "Configuración" se saca del menú hasta que exista la pantalla. Navegaba
    // a /configuracion, ruta que NUNCA se creó: caía en el catch-all de
    // App.jsx y el usuario terminaba en el dashboard sin ninguna explicación,
    // como si la app se hubiera colgado. Un ítem que no lleva a ningún lado es
    // peor que no tenerlo. Cuando haya algo que configurar, se vuelve a poner
    // junto con su ruta — ver PENDIENTES.md.
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Cerrar Sesión',
      danger: true,
    },
  ];

  const handleMenuClick = ({ key }) => {
    // Solo navegar si no es un submenú
    if (!key.includes('-menu')) {
      navigate(key);
      // En mobile el sidebar tapa el contenido: si no se cierra solo, la
      // pantalla recien navegada queda abajo del menú y hace falta un tap de
      // más. Los submenús no cierran nada — todavía no elegiste destino.
      cerrarSiEsMobile();
    }
  };

  const handleUserMenuClick = ({ key }) => {
    if (key === 'logout') {
      logout();
      navigate('/login');
    } else if (key === 'profile') {
      navigate('/mi-perfil');
    }
  };


  return (
    <Layout className="app-layout">
      {/* Sidebar */}
      <FondoSidebarMobile visible={esMobile && !collapsed} onClick={cerrarSiEsMobile} />

      <Sider
        trigger={null}
        collapsible
        {...propsSider}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: zIndexSider,
        }}
      >
        {/* Logo */}
        <div className="logo">
          <h2 className={collapsed ? 'logo-collapsed' : ''}>
            {collapsed ? 'P' : 'PUJA'}
            {!collapsed && <span className="logo-bajada">Gestión de Cabañas</span>}
          </h2>
        </div>
        
        {/* Menú */}
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>

      {/* Layout principal */}
      <Layout style={{ marginLeft: marginLeftContenido, transition: 'margin-left 0.2s' }}>
        {/* Header */}
        <Header 
          style={{ 
            padding: '0 24px', 
            background: colorBgContainer,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}
        >
          {/* Botón colapsar */}
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={alternar}
            style={{ fontSize: '16px', width: 64, height: 64 }}
          />

          {/* Campana + usuario */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <BotonActualizar iconSize={20} />
            <Badge
              count={noLeidasCount}
              size="small"
              onClick={() => navigate('/notificaciones')}
              style={{ cursor: 'pointer' }}
            >
              <BellOutlined style={{ fontSize: 20, cursor: 'pointer' }} />
            </Badge>

            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
            >
              <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
                <span style={{ color: '#333' }}>{user?.nom_usuario || 'Usuario'}</span>
              </div>
            </Dropdown>
          </div>
        </Header>

        {/* Contenido */}
        <Content className="app-content">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
